
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import GlobalFaqSection from "@/components/shared/GlobalFaqSection";
import { SegmentSelector } from "@/components/cadastro/SegmentSelector";
import { RegistrationTypeSelector } from "@/components/cadastro/RegistrationTypeSelector";
import { Timeline } from "@/components/cadastro/Timeline";
import { CredenciamentoTypeSelector } from "@/components/cadastro/CredenciamentoTypeSelector";
import { useRegistrationFlow } from "@/hooks/use-registration-flow";
import SEO from "@/components/SEO";
import useAnalytics from "@/hooks/use-analytics";
import { useEffect } from "react";
import { ExternalLink, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

const CadastroExpress = () => {
  const {
    selectedSegment,
    setSelectedSegment,
    registrationType,
    setRegistrationType,
    flowType,
    setFlowType
  } = useRegistrationFlow();

  const { trackPageView, trackButtonClick, trackRegistrationComplete } = useAnalytics();

  // Track page view
  useEffect(() => {
    trackPageView("cadastro_express", "registration_page");
  }, [trackPageView]);

  const getRegistrationLink = () => {
    if (registrationType === "PF") {
      return "https://drcash.cadastro.io/?token=354d6daa07da2204776ce7b25096c239";
    }
    return "https://drcash.cadastro.io/?token=a328648939b34e58cd2c9418016a1a53";
  };

  const getAssistedRegistrationLink = () => {
    return "https://portal.drcash.com.br/new-lead";
  };

  // SEO structured data for OfferCatalog
  const registrationSchema = {
    "@context": "https://schema.org",
    "@type": "ServiceChannel",
    "name": "Cadastro Expresso Dr.Cash",
    "description": "Faça o credenciamento expresso da sua clínica para o Boleto Parcelado da Dr.Cash de forma rápida e prática.",
    "availableLanguage": "Portuguese",
    "processingTime": "PT10M",
    "serviceType": "Credenciamento Boleto Parcelado",
    "serviceUrl": "https://drcash.com.br/cadastro-express",
    "serviceLocation": {
      "@type": "Place",
      "name": "Online"
    }
  };

  const handleCadastroClick = () => {
    trackButtonClick("cadastro_proceed", "cadastro_express");
    trackRegistrationComplete(registrationType || "");
  };

  const handleAssistedClick = () => {
    trackButtonClick("cadastro_assisted", "cadastro_express");
    trackRegistrationComplete("assisted");
  };

  return (
    <>
      <SEO
        title="Cadastro Expresso | Credenciamento Boleto Parcelado | Dr.Cash"
        description="Cadastre-se no sistema de Boleto Parcelado da Dr.Cash. Escolha entre o credenciamento expresso ou assistido para sua clínica."
        keywords="cadastro drcash, credenciamento boleto parcelado, cadastro express clínica, boleto parcelado dr.cash, registro clínica médica"
        canonical="/cadastro-express"
        structuredData={registrationSchema}
      />
      <Navbar />
      <main className="bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto space-y-16">
            {/* Header Section */}
            <div className="text-center">
              <h1 className="text-4xl font-bold text-drcash-maastricht-blue mb-5 pt-16">
                Escolha como deseja fazer seu credenciamento
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Selecione a opção que melhor atende às suas necessidades.
              </p>
            </div>

            {/* Registration Flow */}
            <div className="space-y-12">
              {/* Credenciamento Type Selector - Always shown */}
              <CredenciamentoTypeSelector 
                selectedType={flowType}
                onTypeSelect={(type) => {
                  setFlowType(type);
                  trackButtonClick("flow_type_select", `flow_${type}`);

                  if (type === "assisted") {
                    handleAssistedClick();
                    // Add a short delay before redirecting to show the selection
                    setTimeout(() => {
                      window.location.href = getAssistedRegistrationLink();
                    }, 800);
                  }
                }}
              />

              {/* Timeline & Express Flow - Only shown after express is selected */}
              {flowType === "express" && (
                <div className="animate-fade-in">
                  <Timeline 
                    selectedSegment={selectedSegment} 
                    registrationType={registrationType} 
                    flowType={flowType} 
                  />

                  {!selectedSegment && (
                    <div className="mt-12 animate-fade-in">
                      <SegmentSelector 
                        selectedSegment={selectedSegment} 
                        onSegmentSelect={(segment) => {
                          setSelectedSegment(segment);
                          trackButtonClick("segment_select", `segment_${segment}`);
                        }}
                      />
                    </div>
                  )}

                  {selectedSegment && !registrationType && (
                    <div className="space-y-8 animate-fade-in pt-2">
                      <h2 className="text-2xl font-bold text-center text-drcash-maastricht-blue">
                        Selecione o tipo de cadastro
                      </h2>
                      <RegistrationTypeSelector 
                        selectedType={registrationType} 
                        onTypeSelect={(type) => {
                          setRegistrationType(type);
                          trackButtonClick("registration_type_select", `type_${type}`);
                        }}
                      />
                    </div>
                  )}

                  {registrationType && (
                    <div className="flex justify-center mt-10 animate-fade-in">
                      <Button 
                        className="btn-primary py-3 px-8 text-lg" 
                        onClick={() => {
                          handleCadastroClick();
                          window.location.href = getRegistrationLink();
                        }}
                      >
                        {`Realizar cadastro ${registrationType}`}
                        <ExternalLink className="ml-2 h-5 w-5" />
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* FAQ Section with "Ver mais" button */}
            <div className="space-y-6 pt-8">
              <GlobalFaqSection 
                title="Perguntas Frequentes" 
                defaultCategory="contratacao"
                showCategories={false}
                containerClass="max-w-3xl mx-auto"
                maxItems={4}
              />
              
              <div className="flex justify-center mt-10">
                <Button 
                  asChild 
                  variant="outline"
                  onClick={() => trackButtonClick("ver_mais_faq", "cadastro_faq")}
                >
                  <Link to="/faq">
                    Ver mais perguntas
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default CadastroExpress;
